import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Radio, HardDrive } from 'lucide-react';
import { LOGO_URL } from '../mock';

/**
 * PUBLIC media status page — mobile-friendly, no login required.
 *
 * Reached by scanning the QR label on a media card. Hits
 * `GET /api/public/media/{code}/status` (unauthenticated) and shows the name +
 * one of three big colored status blocks: Ready / Verifying / Needs Offload.
 * The backend collapses "In Use" onto "Needs Offload" defensively so a card
 * currently mid-shoot never gets flagged as safe to wipe.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  Clean: {
    color: '#10b981',
    bg: 'bg-emerald-500',
    fg: 'text-emerald-50',
    Icon: Radio,
    tagline: 'Safe to use.',
  },
  Verifying: {
    color: '#0ea5e9',
    bg: 'bg-sky-500',
    fg: 'text-sky-50',
    Icon: CheckCircle2,
    tagline: 'Footage transferred — wipe next.',
  },
  'Needs Offload': {
    color: '#f43f5e',
    bg: 'bg-rose-500',
    fg: 'text-rose-50',
    Icon: AlertTriangle,
    tagline: 'Do not wipe — footage still on card.',
  },
};

// Format an ISO datetime as a human-friendly relative-time string.
// "just now" / "5 min ago" / "3 h ago" / "12 d ago" / "3 mo ago".
function relativeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days} d ago`;
  const months = Math.floor(days / 30);
  return `${months} mo ago`;
}

export default function MediaStatusPublic() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND}/api/public/media/${code}/status`)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch((e) => {
        if (alive) {
          setError(
            e?.response?.status === 404
              ? `No media found with code ${code}.`
              : 'Could not load status. Check your connection.',
          );
        }
      });
    return () => {
      alive = false;
    };
  }, [code]);

  const style = data ? STATUS_STYLES[data.status] || STATUS_STYLES['Needs Offload'] : null;
  const Icon = style?.Icon || HardDrive;

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col">
      <header className="p-4 border-b border-neutral-900 flex items-center gap-3">
        <img src={LOGO_URL} alt="Whiplash" className="h-6 opacity-80" />
        <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
          Media Status
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md" data-testid="media-public-status">
          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 text-rose-200 p-6 text-center">
              <AlertTriangle size={28} className="mx-auto mb-3 text-rose-400" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!error && !data && (
            <div className="text-center text-neutral-500 text-sm">Loading…</div>
          )}

          {data && style && (
            <>
              <div className="text-center mb-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2">
                  {data.code}
                </div>
                <h1
                  className="text-2xl sm:text-3xl font-bold text-white leading-tight"
                  data-testid="media-public-name"
                >
                  {data.name}
                </h1>
                {(data.capacity || data.format) && (
                  <div className="text-xs text-neutral-400 mt-2">
                    {[data.capacity, data.format].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>

              <div
                className={`${style.bg} ${style.fg} rounded-2xl p-8 text-center shadow-2xl`}
                data-testid={`media-public-status-${data.status.replace(/\s+/g, '-')}`}
              >
                <Icon size={64} className="mx-auto mb-4 opacity-95" strokeWidth={2.5} />
                <div
                  className="text-4xl sm:text-5xl font-black uppercase tracking-wide leading-none"
                  data-testid="media-public-status-label"
                >
                  {data.status}
                </div>
                <div className="mt-4 text-sm opacity-90 max-w-xs mx-auto">
                  {style.tagline}
                </div>

                {/* Clean state: reveal where the card is if it's currently on a project. */}
                {data.status === 'Clean' && data.checked_out_to && (
                  <div
                    className="mt-5 pt-4 border-t border-white/20 text-xs opacity-90"
                    data-testid="media-public-checked-out"
                  >
                    <div className="text-[10px] tracking-wider uppercase opacity-75 mb-1">
                      Checked out to
                    </div>
                    <div className="font-semibold">{data.checked_out_to}</div>
                  </div>
                )}

                {/* Needs Offload state: how long since the previous transfer, so the
                    operator can spot cards that have been sitting around. */}
                {data.status === 'Needs Offload' && (
                  <div
                    className="mt-5 pt-4 border-t border-white/20 text-xs opacity-90"
                    data-testid="media-public-last-offload"
                  >
                    <div className="text-[10px] tracking-wider uppercase opacity-75 mb-1">
                      Last transferred
                    </div>
                    <div className="font-semibold">
                      {relativeAgo(data.last_offload_at) || 'never'}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center text-[10px] text-neutral-600 mt-6 tracking-wider uppercase">
                Whiplash Film Creative · Gear
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
